import { useCallback, useEffect, useRef, useState } from 'react';

import {
  INLINE_CONFIRMATION_FADE_MS,
  INLINE_CONFIRMATION_MS,
} from '../ai-assistant.constants';

/** `idle` → `shown` (fade in) → `leaving` (fade out) → `idle`. */
export type ConfirmationPhase = 'idle' | 'shown' | 'leaving';

export interface UseTimedConfirmationResult {
  phase: ConfirmationPhase;
  /** Starts the confirmation cycle; restarts if already active. */
  showConfirmation: () => void;
}

/** Timed visual confirmation phases for inline chat actions. */
export const useTimedConfirmation = (): UseTimedConfirmationResult => {
  const [phase, setPhase] = useState<ConfirmationPhase>('idle');
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearConfirmationTimeouts = (): void => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const showConfirmation = useCallback((): void => {
    clearConfirmationTimeouts();
    setPhase('shown');

    timeoutsRef.current = [
      setTimeout(() => setPhase('leaving'), INLINE_CONFIRMATION_MS),
      setTimeout(
        () => setPhase('idle'),
        INLINE_CONFIRMATION_MS + INLINE_CONFIRMATION_FADE_MS
      ),
    ];
  }, []);

  useEffect(
    () => () => {
      clearConfirmationTimeouts();
    },
    []
  );

  return { phase, showConfirmation };
};
